import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ClientEvent, MatrixEventEvent } from "matrix-js-sdk";
import { EventStatus } from "matrix-js-sdk/lib/models/event-status";
import { API_URL } from "src/scenes/App";
import { Toast } from "src/components";
import {
    getCallParticipantLabel,
    getSelectedCustomBackgroundUrl,
    normalizeBackgroundEffect
} from "src/scenes/Chat/components/call/ChatCallPanel.constants";
import {
    applyVirtualBackgroundToStream,
    createVirtualBackgroundVideoTrack
} from "src/scenes/Chat/components/call/virtualBackgroundProcessor";
import {
    MatrixRtcLiveKitSession,
    waitForMatrixRtcRoom
} from "src/scenes/Chat/services/MatrixRtcLiveKitSession";
import { getOrCreateCallWindowBridge } from "src/scenes/Chat/services/CallWindowBridge";
import {
    createInstantMeeting,
    getMeeting,
    joinInternalMeeting,
    leaveInternalMeeting,
    startMeeting
} from "src/api/meetingsApi";
import {
    CALL_NOTE,
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_MEMBER,
    MATRIX_RTC_NOTIFICATION,
    ROOM_MESSAGE
} from "src/constants/matrixEventTypes";
import { UserKeys } from "src/constants/userDetails";
import {
    createMeetingEventMetadata,
    createSharedCallSummary,
    getLatestMembershipExpiry,
    getMeetingEventMetadata,
    getMeetingRecipientIds,
    getSharedCallSummary,
    getSharedCallSummaryUpdate,
    MEETING_EVENT_METADATA_FIELD,
    MEETING_RING_LIFETIME_MS,
    SHARED_CALL_SUMMARY_FIELD,
    WEB_CALL_SUMMARY_FIELD
} from "src/scenes/Chat/utils/meetingCallEventContract";
import { SCREEN_SHARE_FAILURE_MESSAGE } from "src/scenes/Chat/services/ScreenShareHandover";
import { sendMeetingNotifications } from "src/scenes/Chat/utils/meetingCallNotifications";

const CALL_EVENT_TYPES = {
    NOTE: CALL_NOTE,
    LEGACY_NOTE: ROOM_MESSAGE,
    NOTIFY: MATRIX_RTC_CALL_NOTIFY
};

const CALL_STATES = {
    IDLE: "idle",
    OUTBOUND: "outbound",
    INBOUND: "inbound",
    ON_CALL: "on_call"
};

const INITIAL_CALL_STATE = {
    status: CALL_STATES.IDLE,
    callId: "",
    callIds: [],
    callType: "video",
    direction: "",
    sender: "",
    eventId: "",
    isGroupCall: false,
    groupCallId: "",
    matrixRoomId: "",
    chatUuid: "",
    startedAt: 0
};

const INITIAL_CALL_LOBBY_STATE = {
    isOpen: false,
    chat: null,
    popupBlocked: false
};

const APP_CALL_METADATA_FIELD = "com.memotivationapp.call";
const GROUP_VIDEO_CONSTRAINTS = {
    width: { ideal: 640, max: 640 },
    height: { ideal: 360, max: 360 },
    frameRate: { ideal: 15, max: 20 }
};
const PLACEHOLDER_VIDEO_WIDTH = 640;
const PLACEHOLDER_VIDEO_HEIGHT = 360;
const VIDEO_CALL_TYPE = "video";
const CALL_NOTE_MESSAGE_FIELD = "com.memotivationapp.call_note";
const CALL_SUMMARY_MESSAGE_FIELD = WEB_CALL_SUMMARY_FIELD;

const normalizeDeviceIdForConstraints = deviceId => {
    if (!deviceId || deviceId === "default" || String(deviceId).startsWith("active-preview")) {
        return "";
    }

    return deviceId;
};

const isFirefox = () =>
    /firefox/i.test(window.navigator?.userAgent || "");

const getDisplayMediaFromWindow = async gestureWindow => {
    const targetWindow = gestureWindow || window;
    const mediaDevices = targetWindow?.navigator?.mediaDevices;

    if (!mediaDevices?.getDisplayMedia) {
        throw new Error(
            "Screen sharing is not available in this browser."
        );
    }

    return mediaDevices.getDisplayMedia({
        video: true
    });
};

const makeCallId = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getInitialsFromName = (name = "") => {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join("")
        .toUpperCase() || "ME";
};

const createPlaceholderVideoTrack = (initials = "ME") => {
    const canvas = document.createElement("canvas");
    canvas.width = PLACEHOLDER_VIDEO_WIDTH;
    canvas.height = PLACEHOLDER_VIDEO_HEIGHT;
    const context = canvas.getContext("2d");

    context.fillStyle = "#111827";
    context.fillRect(0, 0, canvas.width, canvas.height);
    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, "#0B66E4");
    gradient.addColorStop(1, "#7C3AED");
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(canvas.width / 2, canvas.height / 2, 70, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#ffffff";
    context.font = "800 42px sans-serif";
    context.textAlign = "center";
    context.fillText(initials, canvas.width / 2, canvas.height / 2 + 14);

    const stream = canvas.captureStream(5);
    const [track] = stream.getVideoTracks();
    track.__meBusinessPlaceholder = true;
    return track;
};

const getCallNoteFromContent = content => {
    const note = content?.note || content?.[CALL_NOTE_MESSAGE_FIELD] || {};
    const callId = content?.call_id || note?.call_id || "";

    if (!callId || !note?.body) {
        return null;
    }

    return {
        id: note.id || content.event_id || `${callId}-${content.created_at || Date.now()}`,
        callId,
        body: note.body,
        authorName: content.author_display_name || "",
        authorUserId: content.author_user_id || "",
        createdAt: note.created_at || content.created_at || Date.now(),
        matrixRoomId: content.matrix_room_id || "",
        referenceEventId: content?.["m.relates_to"]?.event_id || ""
    };
};

export const useChatCalls = (matrixClient, headers, openedMatrixChat, activeChatList = [], setOpenedMatrixChat = null, currentUser = null) => {
    const matrixRtcSessionRef = useRef(null);
    const callParticipantIdsRef = useRef(new Set());
    const knownChatsByRoomIdRef = useRef(new Map());
    const callStateRef = useRef(INITIAL_CALL_STATE);
    const activeGroupCallRef = useRef(null);
    const localStreamRef = useRef(null);
    const activeCallChatRef = useRef(null);
    const callWindowRef = useRef(null);
    const callWindowBridgeRef = useRef(getOrCreateCallWindowBridge());
    const pendingCallNotesRef = useRef(0);
    const hasNotesPublicationByRoomRef = useRef(new Map());
    const meetingPreparationRequestIdRef = useRef(0);
    const endedCallIdsRef = useRef(new Set());
    const placeholderVideoTrackRef = useRef(null);
    const screenShareTrackRef = useRef(null);
    const screenShareStopInProgressRef = useRef(false);
    const screenShareStartInProgressRef = useRef(false);
    const cameraOffPreviousVideoTrackRef = useRef(null);
    const mediaPreferencesRef = useRef({
        microphoneEnabled: true,
        cameraEnabled: true,
        microphoneDeviceId: "",
        cameraDeviceId: "",
        speakerDeviceId: "",
        backgroundEffect: "none",
        customBackgroundUrl: ""
    });

    const [localStream, setLocalStream] = useState(null);
    const [localScreenShareStream, setLocalScreenShareStream] = useState(null);
    const [remoteStreams, setRemoteStreams] = useState([]);
    const [callState, setCallState] = useState(INITIAL_CALL_STATE);
    const [activeGroupCall, setActiveGroupCall] = useState(null);
    const [callChat, setCallChat] = useState(null);
    const [callLobby, setCallLobby] = useState(INITIAL_CALL_LOBBY_STATE);
    const [isSendingCallAction, setIsSendingCallAction] = useState(false);
    const [screenShareIsActive, setScreenShareIsActive] = useState(false);
    const [callNotesByCallId, setCallNotesByCallId] = useState({});
    const [isSendingCallNote, setIsSendingCallNote] = useState(false);

    const currentUserId = matrixClient?.getUserId?.() || "";
    
    const replaceVideoSenderTrack = useCallback(async nextTrack => {
        if (!matrixRtcSessionRef.current) {
            return;
        }
        await matrixRtcSessionRef.current.replaceTrack(nextTrack);
    }, []);

    const updateCallState = useCallback((nextStateOrUpdater) => {
        const previousState = callStateRef.current;
        const nextState = typeof nextStateOrUpdater === "function"
            ? nextStateOrUpdater(previousState)
            : nextStateOrUpdater;

        if (nextState === previousState) {
            return previousState;
        }
        callStateRef.current = nextState;
        setCallState(nextState);
        return nextState;
    }, []);

    const getCurrentCallNotesKey = useCallback((state = callStateRef.current) => {
        return state?.groupCallId || state?.callId || "";
    }, []);

    const upsertCallNote = useCallback((note) => {
        if (!note?.callId || !note?.body) {
            return;
        }

        setCallNotesByCallId(previous => {
            const existingNotes = previous[note.callId] || [];
            const existingNote = existingNotes.find(existing => existing.id === note.id);
            if (
                existingNote &&
                existingNote.body === note.body &&
                existingNote.authorUserId === note.authorUserId &&
                existingNote.createdAt === note.createdAt &&
                existingNote.matrixRoomId === note.matrixRoomId &&
                existingNote.referenceEventId === note.referenceEventId
            ) {
                return previous;
            }
            const nextNotesById = new Map(existingNotes.map(existingNote => [existingNote.id, existingNote]));
            nextNotesById.set(note.id, note);

            return {
                ...previous,
                [note.callId]: Array.from(nextNotesById.values())
                    .sort((firstNote, secondNote) => Number(firstNote.createdAt) - Number(secondNote.createdAt))
            };
        });
    }, []);

    const removeCallNote = useCallback(note => {
        if (!note?.callId || !note?.id) {
            return;
        }

        setCallNotesByCallId(previous => Object.fromEntries(
            Object.entries(previous).map(([callId, notes]) => [
                callId,
                notes.filter(existingNote => existingNote.id !== note.id)
            ])
        ));
    }, []);

    useEffect(() => {
        callStateRef.current = callState;
    }, [callState]);

    const rememberKnownChats = useCallback((chats = []) => {
        chats
            .filter(chat => chat?.matrixRoomId)
            .forEach(chat => knownChatsByRoomIdRef.current.set(chat.matrixRoomId, chat));
    }, []);

    useEffect(() => {
        rememberKnownChats([openedMatrixChat, ...(activeChatList || [])]);
    }, [activeChatList, openedMatrixChat, rememberKnownChats]);

    const getChatByRoomId = useCallback((matrixRoomId) => {
        if (!matrixRoomId) {
            return null;
        }

        if (openedMatrixChat?.matrixRoomId === matrixRoomId) {
            return openedMatrixChat;
        }

        return activeChatList.find(chat => chat.matrixRoomId === matrixRoomId) ||
            knownChatsByRoomIdRef.current.get(matrixRoomId) ||
            null;
    }, [activeChatList, openedMatrixChat]);

    const setActiveCallChat = useCallback((chat) => {
        activeCallChatRef.current = chat || null;
        setCallChat(chat || null);
    }, []);

    const getActiveCallChat = useCallback(() => {
        const activeCallState = callStateRef.current || {};
        return activeCallChatRef.current ||
            getChatByRoomId(activeCallState.matrixRoomId) ||
            openedMatrixChat ||
            null;
    }, [getChatByRoomId, openedMatrixChat]);

    const getCurrentUserInitials = useCallback(() => {
        const activeChat = getActiveCallChat();
        const currentUser = activeChat?.users?.find(user => user.matrixUserId === currentUserId);
        const fullName = `${currentUser?.employeeDetails?.firstName || currentUser?.firstName || ""} ${currentUser?.employeeDetails?.lastName || currentUser?.lastName || ""}`.trim();
        return getInitialsFromName(fullName);
    }, [currentUserId, getActiveCallChat]);

    const navigateToCallChat = useCallback(() => {
        const nextChat = getActiveCallChat();

        if (nextChat && nextChat.uuid !== openedMatrixChat?.uuid) {
            setOpenedMatrixChat?.(nextChat);
        }

        return nextChat;
    }, [getActiveCallChat, openedMatrixChat?.uuid, setOpenedMatrixChat]);

    const updateActiveGroupCall = useCallback((nextGroupCall) => {
        activeGroupCallRef.current = nextGroupCall;
        setActiveGroupCall(nextGroupCall);
    }, []);

    const openDedicatedCallWindow = useCallback((options = {}) => {
        if (callWindowRef.current && !callWindowRef.current.closed) {
            callWindowRef.current.focus();
            return true;
        }

        if (options.createIfMissing === false) {
            return false;
        }

        if (!options.descriptor) {
            return false;
        }

        const callToken = callWindowBridgeRef.current.registerCall(options.descriptor);
        const callWindowUrl = `/call-window?callToken=${encodeURIComponent(callToken)}`;
        const nextWindow = window.open(callWindowUrl, "me-business-call-window", "width=1120,height=760,resizable=yes");
        if (!nextWindow) {
            setCallLobby(previous => ({
                ...previous,
                popupBlocked: true
            }));
            if (!options.silent) {
                Toast.error("We couldn't open the call window. Allow pop-ups for Me Business and try again.");
            }
            return false;
        }

        callWindowRef.current = nextWindow;
        setCallLobby(previous => ({
            ...previous,
            popupBlocked: false
        }));
        return true;
    }, []);

    const focusCallWindow = useCallback(() => {
        const activeState = callStateRef.current;
        const chat = callLobby.chat || getActiveCallChat();
        return openDedicatedCallWindow({
            descriptor: chat ? {
                type: activeState.status === CALL_STATES.INBOUND ? "inbound" : "outbound",
                chat,
                meetingUuid: chat.meetingUuid,
                sender: activeState.sender || currentUserId,
                eventId: activeState.eventId || "",
                callId: activeState.callId || makeCallId()
            } : null
        });
    }, [callLobby.chat, currentUserId, getActiveCallChat, openDedicatedCallWindow]);

    const openCallLobby = useCallback(async (chat = openedMatrixChat) => {
        if (!chat) {
            Toast.error("Please select a chat first.");
            return false;
        }

        if (callWindowRef.current && !callWindowRef.current.closed) {
            callWindowRef.current.focus();
        }

        setIsSendingCallAction(true);
        const callId = makeCallId();
        const requestId = meetingPreparationRequestIdRef.current + 1;
        meetingPreparationRequestIdRef.current = requestId;
        let preparedMeetingUuid = "";
        try {
            const createdMeeting = await createInstantMeeting(chat.uuid, callId);
            preparedMeetingUuid = createdMeeting.meetingUuid;
            if (meetingPreparationRequestIdRef.current !== requestId) {
                await leaveInternalMeeting(preparedMeetingUuid).catch(() => undefined);
                return false;
            }
            const meeting = createdMeeting.status === "active"
                ? await joinInternalMeeting(createdMeeting.meetingUuid)
                : await startMeeting(createdMeeting.meetingUuid);
            if (meetingPreparationRequestIdRef.current !== requestId) {
                await leaveInternalMeeting(preparedMeetingUuid).catch(() => undefined);
                return false;
            }
            if (meeting.chatUuid !== chat.uuid || !meeting.matrixRoomId) {
                throw new Error("The meeting does not match this Chat call.");
            }
            await matrixClient.joinRoom(meeting.matrixRoomId);
            chat = {
                ...chat,
                matrixRoomId: meeting.matrixRoomId,
                meetingUuid: meeting.meetingUuid,
                meetingUrl: meeting.meetingUrl,
                meetingHostEmployeeUuid: meeting.hostEmployeeUuid,
                meetingCanManage: meeting.canManage,
                originatingMatrixRoomId: chat.matrixRoomId
            };
            if (!openDedicatedCallWindow({
                descriptor: {
                    type: "outbound",
                    chat,
                    meetingUuid: chat.meetingUuid,
                    sender: currentUserId,
                    eventId: "",
                    callId
                }
            })) {
                setActiveCallChat(chat);
                setCallLobby({
                    isOpen: true,
                    chat,
                    popupBlocked: true
                });
                return false;
            }

            setActiveCallChat(chat);
            setCallLobby({
                isOpen: true,
                chat,
                popupBlocked: false
            });
            return true;
        } catch (error) {
            if (preparedMeetingUuid && meetingPreparationRequestIdRef.current !== requestId) {
                await leaveInternalMeeting(preparedMeetingUuid).catch(() => undefined);
                return false;
            }
            Toast.error(error.getApiErrorDetails?.().message || error.message || "Unable to prepare the meeting.");
            return false;
        } finally {
            setIsSendingCallAction(false);
        }
    }, [
        callWindowRef,
        currentUser,
        currentUserId,
        matrixClient,
        openDedicatedCallWindow,
        openedMatrixChat,
        setActiveCallChat
    ]);

    const closeCallLobby = useCallback(() => {
        meetingPreparationRequestIdRef.current += 1;
        setCallLobby(INITIAL_CALL_LOBBY_STATE);
        if (callStateRef.current.status === CALL_STATES.IDLE && callWindowRef.current && !callWindowRef.current.closed) {
            callWindowRef.current.__meBusinessCallIsClosing = true;
            callWindowRef.current.close();
        }
    }, []);

    const setCallMediaPreferences = useCallback((preferences) => {
        mediaPreferencesRef.current = {
            ...mediaPreferencesRef.current,
            ...preferences,
            microphoneDeviceId: normalizeDeviceIdForConstraints(preferences.microphoneDeviceId ?? mediaPreferencesRef.current.microphoneDeviceId),
            cameraDeviceId: normalizeDeviceIdForConstraints(preferences.cameraDeviceId ?? mediaPreferencesRef.current.cameraDeviceId),
            backgroundEffect: normalizeBackgroundEffect(preferences.backgroundEffect ?? mediaPreferencesRef.current.backgroundEffect),
            customBackgroundUrl: preferences.customBackgroundUrl ?? mediaPreferencesRef.current.customBackgroundUrl ?? ""
        };
    }, []);

    const prepareMatrixRtcRoom = useCallback(async chat => {
        if (!chat?.uuid) {
            throw new Error("The chat is unavailable.");
        }

        try {
            if (chat.meetingUuid) {
                const meeting = await getMeeting(chat.meetingUuid);
                if (meeting.chatUuid !== chat.uuid || meeting.matrixRoomId !== chat.matrixRoomId) {
                    throw new Error("The meeting does not match this Chat call.");
                }
                const joinedMeeting = await joinInternalMeeting(chat.meetingUuid);
                if (joinedMeeting.chatUuid !== chat.uuid || joinedMeeting.matrixRoomId !== chat.matrixRoomId) {
                    throw new Error("The joined meeting does not match this Chat call.");
                }
                if (matrixClient.getRoom(chat.matrixRoomId)?.getMyMembership?.() !== "join") {
                    await matrixClient.joinRoom(chat.matrixRoomId);
                }
                return waitForMatrixRtcRoom(matrixClient, chat.matrixRoomId);
            }
            await axios.post(
                `${API_URL}/chats/${chat.uuid}/calls/prepare`,
                {},
                headers
            );
        } catch (error) {
            console.warn("Unable to prepare the Matrix room for calling.", error);
            throw new Error(error.getApiErrorDetails?.().message || "Unable to prepare the chat for calling.");
        }
    }, [headers, matrixClient]);

    const publishHasNotesUpdate = useCallback(async ({ targetChat, callId, noteRoomId }) => {
        if (!matrixClient || !callId || !noteRoomId) {
            return;
        }
        const publicationKey = `${noteRoomId}:${callId}`;
        const existingStatus = hasNotesPublicationByRoomRef.current.get(publicationKey);
        if (["pending", "published"].includes(existingStatus)) {
            return;
        }

        hasNotesPublicationByRoomRef.current.set(publicationKey, "pending");
        const notesSummaryRoomId = targetChat?.originatingMatrixRoomId || noteRoomId;
        const meetingMetadata = targetChat?.meetingUuid && targetChat?.originatingMatrixRoomId
            ? createMeetingEventMetadata({
                meetingUuid: targetChat.meetingUuid,
                callId,
                chatUuid: targetChat.uuid,
                chatRoomId: targetChat.originatingMatrixRoomId,
                matrixRoomId: noteRoomId
            })
            : undefined;
        try {
            await sendMeetingNotifications(matrixClient, {
                callId,
                lifetime: 0,
                meetingMetadata,
                mode: "notification",
                recipientIds: [],
                roomId: notesSummaryRoomId,
                senderTimestamp: Date.now(),
                summary: { hasNotes: true }
            });
            hasNotesPublicationByRoomRef.current.set(publicationKey, "published");
        } catch (error) {
            hasNotesPublicationByRoomRef.current.delete(publicationKey);
            console.warn("Unable to publish the call notes status update.", error);
        }
    }, [matrixClient]);

    const addCallNote = useCallback(async (body) => {
        const trimmedBody = body?.trim?.() || "";
        const activeCallState = callStateRef.current || {};
        const targetChat = getActiveCallChat();
        const callId = getCurrentCallNotesKey(activeCallState);
        const isMeetingNote = Boolean(targetChat?.originatingMatrixRoomId);
        const noteRoomId = targetChat?.matrixRoomId;

        if (!trimmedBody) {
            return false;
        }

        const canAddNote = [CALL_STATES.OUTBOUND, CALL_STATES.ON_CALL].includes(activeCallState.status);
        if (!matrixClient || !noteRoomId || !callId || !canAddNote) {
            Toast.error("Start or join the call before adding notes.");
            return false;
        }

        const note = {
            id: window.crypto?.randomUUID?.() || `call-note-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            callId,
            body: trimmedBody,
            authorName: matrixClient.getRoom(targetChat?.matrixRoomId)
                ?.getMember(currentUserId)?.name || matrixClient.getUser(currentUserId)?.displayName || "",
            authorUserId: currentUserId,
            createdAt: Date.now(),
            matrixRoomId: noteRoomId,
            referenceEventId: activeCallState.eventId || ""
        };

        pendingCallNotesRef.current += 1;
        setIsSendingCallNote(true);
        upsertCallNote(note);

        try {
            const callReferenceEventId = [callId, activeCallState.eventId]
                .find(eventId => eventId?.startsWith?.("$")) || "";
            const noteContent = isMeetingNote
                ? {
                    msgtype: "m.text",
                    body: trimmedBody
                }
                : {
                    msgtype: "m.text",
                    body: trimmedBody,
                    [CALL_NOTE_MESSAGE_FIELD]: {
                        id: note.id,
                        call_id: callId,
                        body: note.body,
                        created_at: note.createdAt
                    },
                    call_id: callId,
                    author_display_name: note.authorName,
                    author_user_id: currentUserId,
                    created_at: note.createdAt,
                    note: {
                        id: note.id,
                        call_id: callId,
                        body: note.body,
                        created_at: note.createdAt
                    },
                    ...(callReferenceEventId ? {
                        "m.relates_to": {
                            rel_type: "m.reference",
                            event_id: callReferenceEventId
                        }
                    } : {}),
                    [APP_CALL_METADATA_FIELD]: {
                        type: activeCallState.callType || "audio",
                        isGroupCall: Boolean(activeCallState.isGroupCall),
                        groupCallId: activeCallState.groupCallId || ""
                    }
                };
            if (isMeetingNote) {
                await matrixClient.sendEvent(noteRoomId, ROOM_MESSAGE, noteContent, note.id);
            } else {
                await matrixClient.sendEvent(noteRoomId, CALL_EVENT_TYPES.LEGACY_NOTE, noteContent);
            }
            updateCallState(previous => ({ ...previous, hasNotes: true }));
            updateActiveGroupCall(previous => previous ? { ...previous, hasNotes: true } : previous);
            await publishHasNotesUpdate({ targetChat, callId, noteRoomId });
            return true;
        } catch (error) {
            removeCallNote(note);
            console.warn("Unable to add call note.", error);
            Toast.error("Unable to add the call note.");
            return false;
        } finally {
            pendingCallNotesRef.current = Math.max(0, pendingCallNotesRef.current - 1);
            setIsSendingCallNote(pendingCallNotesRef.current > 0);
        }
    }, [
        currentUserId,
        getActiveCallChat,
        getCurrentCallNotesKey,
        matrixClient,
        publishHasNotesUpdate,
        removeCallNote,
        updateActiveGroupCall,
        updateCallState,
        upsertCallNote
    ]);

    const stopMedia = useCallback(() => {
        matrixRtcSessionRef.current?.disconnect().catch(error => {
            console.warn("Unable to leave the MatrixRTC session cleanly.", error);
        });
        matrixRtcSessionRef.current = null;
        localStreamRef.current?.getTracks?.().forEach(track => {
            if (track.__meBusinessVirtualBackgroundCleanup) {
                track.__meBusinessVirtualBackgroundCleanup();
                return;
            }
            track.stop();
        });
        localStreamRef.current = null;
        placeholderVideoTrackRef.current?.stop?.();
        placeholderVideoTrackRef.current = null;
        screenShareTrackRef.current?.stop?.();
        screenShareTrackRef.current = null;
        setLocalStream(null);
        setLocalScreenShareStream(null);
        setRemoteStreams([]);
        setScreenShareIsActive(false);
        cameraOffPreviousVideoTrackRef.current
            ?.__meBusinessVirtualBackgroundCleanup?.();

        if (
            cameraOffPreviousVideoTrackRef.current && !cameraOffPreviousVideoTrackRef.current
                .__meBusinessVirtualBackgroundCleanup
        ) {
            cameraOffPreviousVideoTrackRef.current.stop?.();
        }

        cameraOffPreviousVideoTrackRef.current = null;
    }, []);

    const getLocalMedia = useCallback(async () => {
        if (localStreamRef.current) {
            return localStreamRef.current;
        }

        const mediaPreferences = mediaPreferencesRef.current || {};
        const microphoneDeviceId = normalizeDeviceIdForConstraints(mediaPreferences.microphoneDeviceId);
        const cameraDeviceId = normalizeDeviceIdForConstraints(mediaPreferences.cameraDeviceId);
        const audioConstraint = microphoneDeviceId
            ? { deviceId: { exact: microphoneDeviceId } }
            : true;
        const wantsCamera = mediaPreferences.cameraEnabled !== false;
        const videoConstraint = wantsCamera
            ? {
                ...GROUP_VIDEO_CONSTRAINTS,
                ...(cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : {})
            }
            : false;

        let stream = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraint,
                video: videoConstraint
            });
        } catch (error) {
            console.warn("Unable to start camera, joining the call without camera video.", error);
            stream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraint,
                video: false
            });
        }
        if (!stream.getVideoTracks().length) {
            placeholderVideoTrackRef.current?.stop?.();
            placeholderVideoTrackRef.current = createPlaceholderVideoTrack(getCurrentUserInitials());
            stream.addTrack(placeholderVideoTrackRef.current);
        }
        stream.getAudioTracks().forEach(track => {
            track.enabled = mediaPreferences.microphoneEnabled !== false;
        });
        localStreamRef.current = stream;
        setLocalStream(stream);
        return stream;
    }, [getCurrentUserInitials]);

    const replaceLocalVideoTrackForPeers = useCallback(async (nextTrack) => {
        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return;
        }

        activeStream.getVideoTracks()
            .filter(track => track.id !== nextTrack.id)
            .forEach(track => {
                activeStream.removeTrack(track);
                if (track.__meBusinessVirtualBackgroundCleanup) {
                    track.__meBusinessVirtualBackgroundCleanup();
                    return;
                }
                if (!track.__meBusinessPlaceholder && !track.__meBusinessRetainedForVirtualBackground) {
                    track.stop();
                }
            });

        if (!activeStream.getVideoTracks().some(track => track.id === nextTrack.id)) {
            activeStream.addTrack(nextTrack);
        }

        await matrixRtcSessionRef.current?.replaceTrack(nextTrack);

        setLocalStream(new MediaStream(activeStream.getTracks()));
    }, []);

    const replaceLocalAudioTrackForPeers = useCallback(async (nextTrack) => {
        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return;
        }

        activeStream.getAudioTracks()
            .filter(track => track.id !== nextTrack.id)
            .forEach(track => {
                activeStream.removeTrack(track);
                track.stop();
            });

        if (!activeStream.getAudioTracks().some(track => track.id === nextTrack.id)) {
            activeStream.addTrack(nextTrack);
        }

        await matrixRtcSessionRef.current?.replaceTrack(nextTrack);

        setLocalStream(new MediaStream(activeStream.getTracks()));
    }, []);

    const setLocalAudioEnabled = useCallback((enabled) => {
        localStreamRef.current?.getAudioTracks?.().forEach(track => {
            track.enabled = enabled;
        });
        setLocalStream(previous => previous ? new MediaStream(previous.getTracks()) : previous);
        matrixRtcSessionRef.current?.setMicrophoneEnabled(enabled).catch(error => {
            console.warn("Unable to update the LiveKit microphone state.", error);
        });
    }, []);

    const setLocalMicrophoneDevice = useCallback(async (microphoneDeviceId = "") => {
        const nextMicrophoneDeviceId = normalizeDeviceIdForConstraints(microphoneDeviceId);
        setCallMediaPreferences({ microphoneDeviceId: nextMicrophoneDeviceId });
        window.localStorage?.setItem?.("meBusinessCallMicrophone", nextMicrophoneDeviceId);

        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return true;
        }

        const audioWasEnabled = activeStream.getAudioTracks().some(track => track.enabled !== false);
        try {
            const microphoneStream = await navigator.mediaDevices.getUserMedia({
                audio: nextMicrophoneDeviceId ? { deviceId: { exact: nextMicrophoneDeviceId } } : true,
                video: false
            });
            const [microphoneTrack] = microphoneStream.getAudioTracks();
            if (!microphoneTrack) {
                throw new Error("Microphone did not return an audio track.");
            }
            microphoneTrack.enabled = audioWasEnabled;
            await replaceLocalAudioTrackForPeers(microphoneTrack);
            return true;
        } catch (error) {
            console.warn("Unable to change microphone.", error);
            Toast.error("Unable to change microphone. Check browser permissions or device availability.");
            return false;
        }
    }, [replaceLocalAudioTrackForPeers, setCallMediaPreferences]);

    const setLocalVideoEnabled = useCallback(async enabled => {
        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return false;
        }

        if (!enabled) {
            const [currentVideoTrack] = activeStream.getVideoTracks().filter(track => !track.__meBusinessPlaceholder);

            if (isFirefox() && currentVideoTrack) {
                cameraOffPreviousVideoTrackRef.current = currentVideoTrack;

                // Stop frames from the real camera/processed track.
                currentVideoTrack.enabled = false;

                placeholderVideoTrackRef.current?.stop?.();
                placeholderVideoTrackRef.current = createPlaceholderVideoTrack(getCurrentUserInitials());
                await replaceVideoSenderTrack(placeholderVideoTrackRef.current);

                const nextStream = new MediaStream([
                    ...activeStream.getAudioTracks(),
                    placeholderVideoTrackRef.current
                ]);

                localStreamRef.current = nextStream;
                setLocalStream(nextStream);
                return true;
            }

            placeholderVideoTrackRef.current?.stop?.();
            placeholderVideoTrackRef.current =
            createPlaceholderVideoTrack(getCurrentUserInitials());

            await replaceLocalVideoTrackForPeers(
                placeholderVideoTrackRef.current
            );

            return true;
        }

        /*
        * Firefox:
        * restore the SAME track instead of calling getUserMedia again.
        */
        if (isFirefox()) {
            const previousTrack =
            cameraOffPreviousVideoTrackRef.current;
            if (previousTrack && previousTrack.readyState === "live") {
                previousTrack.enabled = true;
                await replaceVideoSenderTrack(previousTrack);
                const nextStream = new MediaStream([ ...activeStream.getAudioTracks(),previousTrack]);
                localStreamRef.current = nextStream;
                setLocalStream(nextStream);
                placeholderVideoTrackRef.current?.stop?.();
                placeholderVideoTrackRef.current = null;
                cameraOffPreviousVideoTrackRef.current = null;

                return true;
            }
        }

        /*
        * Normal browser path / Firefox fallback.
        */
        const mediaPreferences =
        mediaPreferencesRef.current || {};

        try {
            const cameraDeviceId = normalizeDeviceIdForConstraints(mediaPreferences.cameraDeviceId);
            const cameraStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    ...GROUP_VIDEO_CONSTRAINTS,
                    ...(cameraDeviceId
                        ? {
                            deviceId: {
                                exact: cameraDeviceId
                            }
                        }
                        : {})
                }
            });
            const [cameraTrack] =
            cameraStream.getVideoTracks();
            if (!cameraTrack) {
                throw new Error("Camera did not return a video track.");
            }

            const backgroundEffect =
            mediaPreferences.backgroundEffect || "none";

            const nextTrack = backgroundEffect === "none"
                ? cameraTrack
                : await createVirtualBackgroundVideoTrack({
                    customBackgroundUrl:
                        mediaPreferences.customBackgroundUrl ||
                        getSelectedCustomBackgroundUrl(),
                    effect: backgroundEffect,
                    sourceTrack: cameraTrack
                }) || cameraTrack;

            if (nextTrack !== cameraTrack) {
                cameraTrack.__meBusinessRetainedForVirtualBackground =
                true;
            }

            await replaceLocalVideoTrackForPeers(nextTrack);

            placeholderVideoTrackRef.current?.stop?.();
            placeholderVideoTrackRef.current = null;
            cameraOffPreviousVideoTrackRef.current = null;

            return true;
        } catch (error) {
            console.warn("Unable to turn camera on.", error);
            Toast.error("Unable to turn camera on. Check camera permissions or device availability.");
            return false;
        }
    }, [
        getCurrentUserInitials,
        replaceLocalVideoTrackForPeers,
        replaceVideoSenderTrack
    ]);

    const setLocalCameraDevice = useCallback(async (cameraDeviceId = "") => {
        const nextCameraDeviceId = normalizeDeviceIdForConstraints(cameraDeviceId);
        setCallMediaPreferences({ cameraDeviceId: nextCameraDeviceId });
        window.localStorage?.setItem?.("meBusinessCallCamera", nextCameraDeviceId);

        const activeStream = localStreamRef.current;
        const cameraIsEnabled = activeStream
            ?.getVideoTracks?.()
            ?.some(track => track.enabled && !track.__meBusinessPlaceholder) === true;

        if (!activeStream || !cameraIsEnabled || screenShareTrackRef.current) {
            return true;
        }

        try {
            const cameraStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    ...GROUP_VIDEO_CONSTRAINTS,
                    ...(nextCameraDeviceId ? { deviceId: { exact: nextCameraDeviceId } } : {})
                }
            });
            const [cameraTrack] = cameraStream.getVideoTracks();
            if (!cameraTrack) {
                throw new Error("Camera did not return a video track.");
            }
            const backgroundEffect = mediaPreferencesRef.current?.backgroundEffect || "none";
            const nextTrack = backgroundEffect === "none"
                ? cameraTrack
                : await createVirtualBackgroundVideoTrack({
                    customBackgroundUrl: mediaPreferencesRef.current?.customBackgroundUrl || getSelectedCustomBackgroundUrl(),
                    effect: backgroundEffect,
                    sourceTrack: cameraTrack
                }) || cameraTrack;
            if (nextTrack !== cameraTrack) {
                cameraTrack.__meBusinessRetainedForVirtualBackground = true;
            }
            await replaceLocalVideoTrackForPeers(nextTrack);
            return true;
        } catch (error) {
            console.warn("Unable to change camera.", error);
            Toast.error("Unable to change camera. Check browser permissions or device availability.");
            return false;
        }
    }, [replaceLocalVideoTrackForPeers, setCallMediaPreferences]);

    const setLocalSpeakerDevice = useCallback((speakerDeviceId = "") => {
        setCallMediaPreferences({ speakerDeviceId });
        window.localStorage?.setItem?.("meBusinessCallSpeaker", speakerDeviceId);
        return true;
    }, [setCallMediaPreferences]);

    const stopScreenShare = useCallback(async () => {
        const displayTrack = screenShareTrackRef.current;
        if (!displayTrack || screenShareStopInProgressRef.current) {
            return true;
        }
        screenShareStopInProgressRef.current = true;
        displayTrack.onended = null;

        try {
            if (!matrixRtcSessionRef.current) {
                return false;
            }
            await matrixRtcSessionRef.current.stopScreenShare();
            return true;
        } catch (error) {
            console.error("Unable to restore camera after screen sharing.", error);
            return false;
        } finally {
            screenShareTrackRef.current = null;
            setLocalScreenShareStream(null);
            setScreenShareIsActive(false);

            if (displayTrack.readyState !== "ended") {
                displayTrack.stop();
            }
            screenShareStopInProgressRef.current = false;
        }
    }, []);

    const startScreenShare = useCallback(async gestureWindow => {
        const activeStream = localStreamRef.current;
        let displayStream = null;
        let displayTrack = null;
        const session = matrixRtcSessionRef.current;

        if (screenShareStartInProgressRef.current) {
            return false;
        }
        if (!session || !activeStream || callStateRef.current.status !== CALL_STATES.ON_CALL) {
            Toast.error("Join the call before sharing your screen.");
            return false;
        }

        const captureWindow = gestureWindow || window;
        screenShareStartInProgressRef.current = true;
        try {
            displayStream = await getDisplayMediaFromWindow(captureWindow);
            [displayTrack] = displayStream.getVideoTracks();

            if (!displayTrack) {
                throw new Error("Screen sharing did not return a video track.");
            }

            screenShareTrackRef.current = displayTrack;

            /*
            * Only change what WebRTC sends.
            * DO NOT call replaceLocalVideoTrackForPeers here because
            * that function destroys the camera/background track.
            */
            if (!matrixRtcSessionRef.current) {
                throw new Error("The MatrixRTC call is unavailable.");
            }
            if (matrixRtcSessionRef.current !== session) {
                throw new Error("The call changed while preparing screen sharing.");
            }
            await session.startScreenShare(displayTrack);
            if (matrixRtcSessionRef.current !== session || displayTrack.readyState !== "live") {
                throw new Error("Screen sharing ended before it was ready.");
            }

            setLocalScreenShareStream(displayStream);

            displayTrack.onended = () => {
                if (matrixRtcSessionRef.current === session && screenShareTrackRef.current === displayTrack) {
                    stopScreenShare().catch(error => console.warn("Unable to stop ended screen sharing.", error));
                }
            };

            setScreenShareIsActive(true);
            return true;
        } catch (error) {
            if (displayTrack && session.screenShareCapture === displayTrack) {
                await session.stopScreenShare().catch(stopError => {
                    console.warn("Unable to clean up failed screen sharing.", stopError);
                });
            }
            if (matrixRtcSessionRef.current === session) {
                screenShareTrackRef.current = null;
                setLocalScreenShareStream(null);
                setScreenShareIsActive(false);
            }
            displayStream?.getTracks?.().forEach(track => track.stop());
            console.error("Unable to start screen sharing.", {
                name: error?.name,
                message: error?.message
            });

            if (error?.name !== "NotAllowedError" && error?.name !== "AbortError") {
                Toast.error(SCREEN_SHARE_FAILURE_MESSAGE);
            }
            return false;
        } finally {
            screenShareStartInProgressRef.current = false;
        }
    }, [stopScreenShare]);

    const toggleScreenShare = useCallback(gestureWindow => {
        if (screenShareTrackRef.current) {
            return stopScreenShare();
        }

        return startScreenShare(gestureWindow);
    }, [startScreenShare, stopScreenShare]);

    const setLocalBackgroundEffect = useCallback(async (backgroundEffect, options = {}) => {
        const nextBackgroundEffect = normalizeBackgroundEffect(backgroundEffect);
        const nextCustomBackgroundUrl =
        options.customBackgroundUrl ?? getSelectedCustomBackgroundUrl();

        setCallMediaPreferences({
            backgroundEffect: nextBackgroundEffect,
            customBackgroundUrl: nextCustomBackgroundUrl
        });

        window.localStorage?.setItem?.(
            "meBusinessCallBackground",
            nextBackgroundEffect
        );

        const activeStream = localStreamRef.current;

        if (!activeStream) {
            return true;
        }

        try {
            /*
             * FIREFOX ONLY
             */
            if (isFirefox()) {
                const [currentVideoTrack] =
                activeStream.getVideoTracks().filter(track => !track.__meBusinessPlaceholder);

                if (!currentVideoTrack) {
                    return true;
                }

                if (
                    nextBackgroundEffect !== "none" && currentVideoTrack.__meBusinessSetVirtualBackground
                ) {
                    await currentVideoTrack.__meBusinessSetVirtualBackground({
                        effect: nextBackgroundEffect,
                        customBackgroundUrl: nextCustomBackgroundUrl
                    });
                    return true;
                }
            }

            // NORMAL PATH
            if (screenShareTrackRef.current) {
                return true;
            }

            const cameraIsEnabled = activeStream.getVideoTracks().some(
                track =>
                    track.enabled &&
                    !track.__meBusinessPlaceholder
            );

            if (!cameraIsEnabled) {
                return true;
            }

            const cameraDeviceId = normalizeDeviceIdForConstraints(
                mediaPreferencesRef.current?.cameraDeviceId
            );

            const cameraStream =
            await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    ...GROUP_VIDEO_CONSTRAINTS,
                    ...(cameraDeviceId
                        ? {
                            deviceId: {
                                exact: cameraDeviceId
                            }
                        }
                        : {})
                }
            });

            const [cameraTrack] = cameraStream.getVideoTracks();
            if (!cameraTrack) {
                throw new Error("Camera did not return a video track.");
            }

            const nextTrack = nextBackgroundEffect === "none"
                ? cameraTrack
                : await createVirtualBackgroundVideoTrack({
                    customBackgroundUrl: nextCustomBackgroundUrl,
                    effect: nextBackgroundEffect,
                    sourceTrack: cameraTrack
                }) || cameraTrack;

            if (nextTrack !== cameraTrack) {
                cameraTrack.__meBusinessRetainedForVirtualBackground = true;
            }

            await replaceLocalVideoTrackForPeers(nextTrack);

            return true;
        } catch (error) {
            console.warn("Unable to change virtual background.",error);
            Toast.error("Unable to change virtual background.");
            return false;
        }
    }, [
        replaceLocalVideoTrackForPeers,
        setCallMediaPreferences
    ]);

    const connectMatrixRtcCall = useCallback(async (chat, options = {}) => {
        if (!matrixClient || !chat?.matrixRoomId) {
            throw new Error("The Matrix room is unavailable.");
        }

        let matrixRoom = matrixClient.getRoom(chat.matrixRoomId);
        if (!matrixRoom && !chat.meetingUuid) {
            throw new Error("The Matrix room has not finished loading.");
        }

        const isOutbound = options.ring === true;
        const initialCallId = isOutbound
            ? makeCallId()
            : callStateRef.current.callId || makeCallId();
        const sender = options.sender || currentUserId;
        if (isOutbound) {
            const connectingState = {
                ...INITIAL_CALL_STATE,
                status: CALL_STATES.OUTBOUND,
                callId: initialCallId,
                callIds: [initialCallId],
                direction: "outbound",
                sender,
                isGroupCall: Boolean(chat.isGroupChat),
                groupCallId: initialCallId,
                matrixRoomId: chat.matrixRoomId,
                chatUuid: chat.uuid,
                hasNotes: false
            };
            updateCallState(connectingState);
            updateActiveGroupCall({ ...connectingState, isActive: true, chat });
            setActiveCallChat(chat);
        }

        const previousSession = matrixRtcSessionRef.current;
        if (previousSession) {
            matrixRtcSessionRef.current = null;
            await previousSession.disconnect();
        }

        const [preparedRoom, stream] = await Promise.all([
            prepareMatrixRtcRoom(chat),
            getLocalMedia()
        ]);
        if (preparedRoom) {
            matrixRoom = preparedRoom;
        }
        if (!matrixRoom) {
            throw new Error("The Matrix room has not finished loading.");
        }
        const meetingMetadata = chat.meetingUuid && chat.originatingMatrixRoomId
            ? createMeetingEventMetadata({
                meetingUuid: chat.meetingUuid,
                callId: initialCallId,
                chatUuid: chat.uuid,
                chatRoomId: chat.originatingMatrixRoomId,
                matrixRoomId: chat.matrixRoomId
            })
            : null;
        const session = new MatrixRtcLiveKitSession({
            matrixClient,
            matrixRoom,
            meetingMetadata,
            onScreenShareStopped: reason => {
                if (matrixRtcSessionRef.current !== session) {
                    return;
                }
                const track = screenShareTrackRef.current;
                if (track) {
                    track.onended = null;
                }
                screenShareTrackRef.current = null;
                setLocalScreenShareStream(null);
                setScreenShareIsActive(false);
                if (reason) {
                    Toast.info(reason);
                }
            },
            onParticipantsChanged: streams => {
                const participants = streams.filter(item => !item.isScreenShare);
                participants.forEach(item => {
                    if (item.peerUserId) {
                        callParticipantIdsRef.current.add(item.peerUserId);
                    }
                });
                setRemoteStreams(streams);
                if (participants.length > 0) {
                    updateCallState(previous => previous.status === CALL_STATES.OUTBOUND
                        ? {
                            ...previous,
                            status: CALL_STATES.ON_CALL,
                            startedAt: Date.now()
                        }
                        : previous
                    );
                }
            },
            onSessionChanged: matrixSession => {
                const sessionCallId = session.ringCallId || callStateRef.current.callId ||
                    session.sessionId || matrixSession.callId;
                updateCallState(previous => {
                    if (
                        previous.callId === sessionCallId &&
                        previous.groupCallId === sessionCallId &&
                        (!sessionCallId || (
                            previous.callIds.length === 1 &&
                            previous.callIds[0] === sessionCallId
                        ))
                    ) {
                        return previous;
                    }

                    return {
                        ...previous,
                        callId: sessionCallId,
                        callIds: sessionCallId ? [sessionCallId] : previous.callIds,
                        groupCallId: sessionCallId
                    };
                });
            }
        });
        session.ringCallId = isOutbound ? initialCallId : "";
        matrixRtcSessionRef.current = session;
        callParticipantIdsRef.current = new Set([currentUserId]);
        try {
            await session.connect({ localStream: stream, ring: isOutbound && !chat.meetingUuid });
            if (isOutbound && meetingMetadata && chat.originatingMatrixRoomId) {
                const callId = session.ringCallId || initialCallId;
                const notification = await sendMeetingNotifications(matrixClient, {
                    additionalContent: {
                        meeting_uuid: chat.meetingUuid,
                        meeting_room_id: chat.matrixRoomId,
                        meeting_url: chat.meetingUrl,
                        meeting_host_employee_uuid: chat.meetingHostEmployeeUuid
                    },
                    callId,
                    lifetime: MEETING_RING_LIFETIME_MS,
                    meetingMetadata,
                    mode: "ring",
                    recipientIds: getMeetingRecipientIds(chat.users, currentUserId),
                    roomId: chat.originatingMatrixRoomId,
                    senderTimestamp: Date.now(),
                    summary: { hasNotes: false }
                });
                session.ringEventId = notification.callNotifyEventId || notification.rtcNotificationEventId;
            }
        } catch (error) {
            if (matrixRtcSessionRef.current === session) {
                matrixRtcSessionRef.current = null;
            }
            throw error;
        }

        const callId = session.ringCallId || callStateRef.current.callId ||
            session.sessionId || makeCallId();
        const hasConnectedParticipant = session.remoteParticipantCount > 0;
        const isConnected = !isOutbound || hasConnectedParticipant;
        const nextState = {
            status: isConnected ? CALL_STATES.ON_CALL : CALL_STATES.OUTBOUND,
            callId,
            callIds: [callId],
            callType: VIDEO_CALL_TYPE,
            direction: isOutbound ? "outbound" : "inbound",
            sender,
            eventId: options.eventId || session.ringEventId || callStateRef.current.eventId || "",
            isGroupCall: Boolean(chat.isGroupChat),
            groupCallId: callId,
            matrixRoomId: chat.matrixRoomId,
            chatUuid: chat.uuid,
            startedAt: isConnected ? Date.now() : 0,
            ...(typeof callStateRef.current.hasNotes === "boolean"
                ? { hasNotes: callStateRef.current.hasNotes }
                : {})
        };
        updateCallState(nextState);
        updateActiveGroupCall({
            ...nextState,
            isActive: true,
            chat
        });
        setActiveCallChat(chat);
        return true;
    }, [
        currentUserId,
        getLocalMedia,
        matrixClient,
        prepareMatrixRtcRoom,
        setActiveCallChat,
        stopScreenShare,
        updateActiveGroupCall,
        updateCallState
    ]);

    const startCall = useCallback(async (chat = openedMatrixChat) => {
        setActiveCallChat(chat);
        setIsSendingCallAction(true);

        try {
            const otherUsers = chat?.users
                ?.map(user => user.matrixUserId)
                ?.filter(matrixUserId => matrixUserId && matrixUserId !== currentUserId) || [];
            if (!otherUsers.length) {
                Toast.error("There is no one else in this chat to call.");
                return;
            }
            await connectMatrixRtcCall(chat, { ring: true });
        } catch (error) {
            console.warn("Unable to start the MatrixRTC call.", error);
            Toast.error(error?.message || "Unable to start the call.");
            stopMedia();
            updateCallState(INITIAL_CALL_STATE);
            updateActiveGroupCall(null);
            setActiveCallChat(null);
            if (callWindowRef.current && !callWindowRef.current.closed) {
                callWindowRef.current.__meBusinessCallIsClosing = true;
                callWindowRef.current.close();
            }
        } finally {
            setIsSendingCallAction(false);
        }
    }, [
        connectMatrixRtcCall,
        currentUserId,
        openedMatrixChat,
        setActiveCallChat,
        stopMedia,
        updateActiveGroupCall,
        updateCallState
    ]);

    const adoptLobbyMediaStream = useCallback(async (settings = {}) => {
        const { mediaStream, ...mediaPreferences } = settings;

        setCallMediaPreferences(mediaPreferences);
        if (mediaStream) {
            let nextMediaStream = mediaStream;
            const backgroundEffect = normalizeBackgroundEffect(mediaPreferences.backgroundEffect);
            if (
                backgroundEffect !== "none"
                && nextMediaStream.getVideoTracks().some(track => !track.__meBusinessPlaceholder)
            ) {
                try {
                    nextMediaStream = await applyVirtualBackgroundToStream({
                        customBackgroundUrl: mediaPreferences.customBackgroundUrl || getSelectedCustomBackgroundUrl(),
                        effect: backgroundEffect,
                        stream: nextMediaStream
                    });
                } catch (error) {
                    console.warn("Unable to apply the initial virtual background.", error);
                    Toast.error("Unable to apply the selected background effect.");
                }
            }
            if (!nextMediaStream.getVideoTracks().length) {
                placeholderVideoTrackRef.current?.stop?.();
                placeholderVideoTrackRef.current = createPlaceholderVideoTrack(getCurrentUserInitials());
                nextMediaStream.addTrack(placeholderVideoTrackRef.current);
            }
            nextMediaStream.getAudioTracks().forEach(track => {
                track.enabled = mediaPreferences.microphoneEnabled !== false;
            });
            localStreamRef.current = nextMediaStream;
            setLocalStream(nextMediaStream);
        }

        return mediaPreferences;
    }, [getCurrentUserInitials, setCallMediaPreferences]);

    const startCallFromLobby = useCallback(async (settings = {}) => {
        const lobbyChat = callLobby.chat || openedMatrixChat;
        await adoptLobbyMediaStream(settings);

        setCallLobby(INITIAL_CALL_LOBBY_STATE);
        await startCall(lobbyChat);
    }, [
        adoptLobbyMediaStream,
        callLobby.chat,
        openedMatrixChat,
        startCall
    ]);

    const joinGroupCall = useCallback(async (chat = null) => {
        const activeCall = activeGroupCallRef.current || callStateRef.current;
        const isRejoining = Boolean(chat) &&
            callStateRef.current.status === CALL_STATES.IDLE &&
            activeCall?.isActive === true;
        const activeCallChat = activeCall.chat ||
            getChatByRoomId(activeCall.matrixRoomId) ||
            chat ||
            navigateToCallChat();
        if (!activeCallChat) {
            Toast.error("Unable to find the active call.");
            return false;
        }

        setActiveCallChat(activeCallChat);
        updateCallState(previous => ({
            ...previous,
            status: CALL_STATES.INBOUND,
            callId: activeCall.callId || activeCall.groupCallId || previous.callId,
            callIds: [activeCall.callId || activeCall.groupCallId || previous.callId].filter(Boolean),
            direction: "inbound",
            sender: activeCall.sender || previous.sender,
            eventId: activeCall.eventId || previous.eventId,
            isGroupCall: Boolean(activeCallChat.isGroupChat),
            groupCallId: activeCall.groupCallId || activeCall.callId || previous.groupCallId,
            matrixRoomId: activeCallChat.matrixRoomId,
            chatUuid: activeCallChat.uuid,
            suppressRingtone: isRejoining
        }));

        return openDedicatedCallWindow({
            descriptor: {
                type: "inbound",
                chat: activeCallChat,
                meetingUuid: activeCallChat.meetingUuid,
                sender: activeCall.sender || "",
                eventId: activeCall.eventId || "",
                callId: activeCall.callId || activeCall.groupCallId || "",
                suppressRingtone: isRejoining
            }
        });
    }, [
        getChatByRoomId,
        navigateToCallChat,
        openDedicatedCallWindow,
        setActiveCallChat,
        updateCallState
    ]);

    const answerCall = useCallback(async () => {
        await joinGroupCall();
    }, [joinGroupCall]);

    const answerCallFromLobby = useCallback(async (settings = {}) => {
        await adoptLobbyMediaStream(settings);
        await answerCall();
    }, [adoptLobbyMediaStream, answerCall]);

    const endCall = useCallback(async (action, reason = null, options = {}) => {
        const activeCallState = callStateRef.current || {};
        const preserveActiveCall = options.preserveActiveCall === true;
        if (!preserveActiveCall) {
            [activeCallState.callId, ...(activeCallState.callIds || [])]
                .filter(Boolean)
                .forEach(callId => endedCallIdsRef.current.add(callId));
        }
        const targetChat = getActiveCallChat();
        const session = matrixRtcSessionRef.current;
        const participantUserIds = Array.from(callParticipantIdsRef.current);
        const summaryRoomId = targetChat?.originatingMatrixRoomId || targetChat?.matrixRoomId;
        const isMeetingCall = Boolean(targetChat?.meetingUuid);
        const isMeetingSummaryPublisher = targetChat?.meetingCanManage === true
            || targetChat?.meetingHostEmployeeUuid === currentUser?.[UserKeys.UUID]
            || targetChat?.meetingHostEmployeeUuid === currentUser?.[UserKeys.EMPLOYEE_UUID]
            || activeCallState.direction === "outbound";
        const isCompletedCall = activeCallState.status === CALL_STATES.ON_CALL &&
            Boolean(activeCallState.startedAt);
        const isDeclinedMeetingCall = isMeetingCall &&
            activeCallState.status === CALL_STATES.INBOUND &&
            ["cancel", "reject"].includes(action);
        const isCancelledMeetingCall = isMeetingCall &&
            activeCallState.status === CALL_STATES.OUTBOUND &&
            action === "cancel";
        const shouldPublishSummary = options.publishSummary !== false &&
            summaryRoomId &&
            (
                isDeclinedMeetingCall
                || isCancelledMeetingCall
                || (isCompletedCall && (
                    isMeetingCall ? isMeetingSummaryPublisher : !session || session.memberships.length <= 1
                ))
            );
        const endedAt = Date.now();
        const membershipParticipantIds = (session?.memberships || [])
            .map(membership => membership.sender)
            .filter(Boolean);
        const allParticipantUserIds = Array.from(new Set([
            ...participantUserIds,
            ...membershipParticipantIds,
            currentUserId
        ].filter(Boolean)));
        const meetingMetadata = isMeetingCall && summaryRoomId
            ? createMeetingEventMetadata({
                meetingUuid: targetChat.meetingUuid,
                callId: activeCallState.callId,
                chatUuid: targetChat.uuid,
                chatRoomId: summaryRoomId,
                matrixRoomId: targetChat.matrixRoomId
            })
            : null;
        const sharedSummary = meetingMetadata && isCompletedCall
            ? createSharedCallSummary({
                startedAt: activeCallState.startedAt,
                ...(session?.remoteParticipantCount > 0 ? {} : { endedAt }),
                expiresAt: getLatestMembershipExpiry(session?.memberships, endedAt),
                participantIds: allParticipantUserIds,
                initiatorId: activeCallState.direction === "outbound"
                    ? currentUserId
                    : activeCallState.sender || currentUserId,
                ...(typeof activeCallState.hasNotes === "boolean"
                    ? { hasNotes: activeCallState.hasNotes }
                    : {})
            })
            : null;
        const sharedSummaryUpdate = sharedSummary || (
            typeof activeCallState.hasNotes === "boolean"
                ? { hasNotes: activeCallState.hasNotes }
                : null
        );

        let summaryPromise = Promise.resolve();
        if (shouldPublishSummary) {
            const durationSeconds = isCompletedCall
                ? Math.max(1, Math.round((Date.now() - activeCallState.startedAt) / 1000))
                : 0;
            const outcome = isDeclinedMeetingCall
                ? "declined"
                : isCancelledMeetingCall ? "cancelled" : "completed";
            summaryPromise = matrixClient.sendEvent(summaryRoomId, "m.room.message", {
                msgtype: "m.notice",
                body: `Call ended after ${durationSeconds} seconds.`,
                ...(meetingMetadata && sharedSummaryUpdate ? {
                    [MEETING_EVENT_METADATA_FIELD]: meetingMetadata,
                    [SHARED_CALL_SUMMARY_FIELD]: sharedSummaryUpdate
                } : {}),
                [WEB_CALL_SUMMARY_FIELD]: {
                    call_id: activeCallState.callId,
                    duration_seconds: durationSeconds,
                    participant_user_ids: participantUserIds,
                    ended_reason: reason || action,
                    ended_by_user_id: currentUserId,
                    outcome,
                    is_group_call: Boolean(activeCallState.isGroupCall)
                },
                ...(activeCallState.eventId ? {
                    "m.relates_to": {
                        rel_type: "m.reference",
                        event_id: activeCallState.eventId
                    }
                } : {})
            })
                .catch(error => {
                    console.warn("Unable to publish the MatrixRTC call summary.", error);
                });
        }

        const publishTerminalNotification = meetingMetadata &&
            options.publishTerminalNotification !== false
            ? () => sendMeetingNotifications(matrixClient, {
                additionalContent: {
                    meeting_uuid: targetChat.meetingUuid,
                    meeting_room_id: targetChat.matrixRoomId
                },
                callId: activeCallState.callId,
                lifetime: 0,
                meetingMetadata,
                mode: "notification",
                recipientIds: [],
                roomId: summaryRoomId,
                senderTimestamp: endedAt,
                summary: sharedSummaryUpdate || undefined
            }).catch(error => {
                console.warn("Unable to publish the MatrixRTC terminal notification.", error);
            })
            : () => Promise.resolve();

        matrixRtcSessionRef.current = null;
        callParticipantIdsRef.current = new Set();

        updateCallState(previous => ({
            ...previous,
            status: CALL_STATES.IDLE,
            callId: "",
            callIds: [],
            matrixRoomId: "",
            chatUuid: "",
            startedAt: 0
        }));
        stopMedia();
        setActiveCallChat(null);
        setCallLobby(INITIAL_CALL_LOBBY_STATE);
        if (callWindowRef.current && !callWindowRef.current.closed) {
            callWindowRef.current.__meBusinessCallIsClosing = true;
            callWindowRef.current.close();
        }
        updateActiveGroupCall(preserveActiveCall ? {
            ...activeCallState,
            callType: VIDEO_CALL_TYPE,
            chat: targetChat,
            isActive: true
        } : null);

        await session?.disconnect().catch(error => {
            console.warn("Unable to leave the MatrixRTC session cleanly.", error);
        });
        if (session && isMeetingCall) {
            await leaveInternalMeeting(targetChat.meetingUuid).catch(error => {
                console.warn("Unable to leave the meeting through the API.", error);
            });
        }
        await Promise.allSettled([
            summaryPromise,
            publishTerminalNotification()
        ]);
    }, [
        currentUser,
        getActiveCallChat,
        matrixClient,
        setActiveCallChat,
        stopMedia,
        updateActiveGroupCall,
        updateCallState
    ]);

    const hangupCall = useCallback(async () => {
        await endCall("hangup", "user_hangup");
    }, [endCall]);

    const rejectCall = useCallback(async () => {
        await endCall("reject");
    }, [endCall]);

    useEffect(() => {
        if (!matrixClient) {
            return undefined;
        }

        const handleMatrixRtcNotification = (event, room, toStartOfTimeline, removed, data) => {
            const eventType = event?.getType?.();
            const content = event.getContent?.() || {};
            const meetingMetadata = getMeetingEventMetadata(content);
            const sharedSummaryUpdate = getSharedCallSummaryUpdate(content);
            if (eventType === MATRIX_RTC_MEMBER) {
                const originatingChat = meetingMetadata
                    ? getChatByRoomId(meetingMetadata.chatRoomId)
                    : null;
                if (originatingChat && meetingMetadata) {
                    knownChatsByRoomIdRef.current.set(meetingMetadata.matrixRoomId, {
                        ...originatingChat,
                        matrixRoomId: meetingMetadata.matrixRoomId,
                        meetingUuid: meetingMetadata.meetingUuid,
                        originatingMatrixRoomId: meetingMetadata.chatRoomId
                    });
                }
                return;
            }
            if (![CALL_EVENT_TYPES.NOTIFY, MATRIX_RTC_NOTIFICATION].includes(eventType)) {
                return;
            }

            const notificationType = content.notify_type || content.notification_type;
            const sender = event.getSender?.() || "";
            const notificationCallId = meetingMetadata?.callId || content.call_id || "";
            const callRoomId = meetingMetadata?.matrixRoomId || event.getRoomId?.() || "";
            const isHasNotesOnlyUpdate = notificationType !== "ring" &&
                typeof sharedSummaryUpdate?.hasNotes === "boolean" &&
                !getSharedCallSummary(content);
            if (typeof sharedSummaryUpdate?.hasNotes === "boolean") {
                const mergeHasNotes = previous => previous === true || sharedSummaryUpdate.hasNotes;
                if (callStateRef.current.matrixRoomId === callRoomId) {
                    updateCallState(previous => ({
                        ...previous,
                        hasNotes: mergeHasNotes(previous.hasNotes)
                    }));
                }
                if (activeGroupCallRef.current?.matrixRoomId === callRoomId) {
                    updateActiveGroupCall(previous => previous ? ({
                        ...previous,
                        hasNotes: mergeHasNotes(previous.hasNotes)
                    }) : previous);
                }
            }
            if (isHasNotesOnlyUpdate) {
                return;
            }
            if (notificationType === "notification") {
                if (notificationCallId) {
                    endedCallIdsRef.current.add(notificationCallId);
                }
                if (
                    sender !== currentUserId &&
                    callStateRef.current.status === CALL_STATES.INBOUND &&
                    callStateRef.current.callId === notificationCallId &&
                    callStateRef.current.sender === sender
                ) {
                    endCall("cancelled", null, {
                        publishSummary: false,
                        publishTerminalNotification: false
                    }).catch(() => undefined);
                }
                return;
            }
            if (["cancel", "reject"].includes(notificationType) && sender !== currentUserId) {
                if (notificationCallId) {
                    endedCallIdsRef.current.add(notificationCallId);
                }
                if (
                    callStateRef.current.status !== CALL_STATES.IDLE
                    && callStateRef.current.callId === notificationCallId
                ) {
                    const action = notificationType === "reject" ? "declined" : "cancelled";
                    endCall(action, null, {
                        publishSummary: false,
                        publishTerminalNotification: false
                    }).catch(() => undefined);
                }
                return;
            }
            if (notificationType !== "ring") {
                return;
            }

            const notificationRoom = room || matrixClient.getRoom(event.getRoomId?.());
            const notificationRoomId = notificationRoom?.roomId || event.getRoomId?.() || "";
            const originatingChatRoomId = meetingMetadata?.chatRoomId || notificationRoomId;
            const originatingChat = getChatByRoomId(originatingChatRoomId);
            const matrixRoomId = meetingMetadata?.matrixRoomId || content.meeting_room_id || notificationRoomId;
            const eventChat = originatingChat && (meetingMetadata || content.meeting_room_id) ? {
                ...originatingChat,
                matrixRoomId,
                meetingUuid: meetingMetadata?.meetingUuid || content.meeting_uuid,
                meetingUrl: content.meeting_url,
                meetingHostEmployeeUuid: content.meeting_host_employee_uuid,
                meetingCanManage: content.meeting_host_employee_uuid === currentUser?.[UserKeys.UUID]
                    || content.meeting_host_employee_uuid === currentUser?.[UserKeys.EMPLOYEE_UUID],
                originatingMatrixRoomId: originatingChatRoomId
            } : originatingChat;
            if (!eventChat) {
                const retryCount = data?.callRetryCount || 0;
                if (retryCount < 20) {
                    window.setTimeout(() => handleMatrixRtcNotification(
                        event,
                        room,
                        toStartOfTimeline,
                        removed,
                        { callRetryCount: retryCount + 1 }
                    ), 500);
                }
                return;
            }

            const activeCallRoom = meetingMetadata || content.meeting_room_id
                ? matrixClient.getRoom(matrixRoomId)
                : notificationRoom;
            const activeSession = activeCallRoom
                ? matrixClient.matrixRTC?.getActiveRoomSession?.(activeCallRoom)
                : null;
            const notificationParentId = content?.["m.relates_to"]?.event_id || "";
            const callId = meetingMetadata?.callId || content.call_id || notificationParentId ||
                activeSession?.callId || activeSession?.getOldestMembership?.()?.eventId || "";
            const eventId = event.getId?.() || "";
            if (sender === currentUserId) {
                if (callStateRef.current.matrixRoomId === matrixRoomId) {
                    updateCallState(previous => ({
                        ...previous,
                        callId: callId || previous.callId,
                        groupCallId: callId || previous.groupCallId,
                        eventId
                    }));
                }
                return;
            }

            const notificationTimestamp = Number(content.sender_ts || event.getTs?.() || 0);
            const notificationLifetime = Number(content.lifetime ?? MEETING_RING_LIFETIME_MS);
            const hasTerminalEvent = notificationRoom
                ?.getUnfilteredTimelineSet?.()
                ?.getLiveTimeline?.()
                ?.getEvents?.()
                ?.some(timelineEvent => {
                    const timelineContent = timelineEvent.getContent?.() || {};
                    const timelineNotificationType = timelineContent.notify_type ||
                        timelineContent.notification_type;
                    const timelineSummaryUpdate = getSharedCallSummaryUpdate(timelineContent);
                    const timelineIsHasNotesOnlyUpdate = typeof timelineSummaryUpdate?.hasNotes === "boolean" &&
                        !getSharedCallSummary(timelineContent);
                    const isMatchingCancellation = ["cancel", "reject", "notification"].includes(
                        timelineNotificationType
                    ) && !timelineIsHasNotesOnlyUpdate &&
                        timelineContent.call_id === callId;
                    const sharedSummary = getSharedCallSummary(timelineContent);
                    const timelineMeetingMetadata = getMeetingEventMetadata(timelineContent);
                    const isMatchingSummary = timelineContent[CALL_SUMMARY_MESSAGE_FIELD]?.call_id === callId ||
                        (Boolean(sharedSummary) && (
                            timelineContent.call_id === callId || timelineMeetingMetadata?.callId === callId
                        ));

                    return timelineEvent.getTs?.() >= notificationTimestamp &&
                        (isMatchingCancellation || isMatchingSummary);
                }) === true;
            const isHistorical = toStartOfTimeline || data?.liveEvent === false ||
                !notificationTimestamp || Date.now() - notificationTimestamp > notificationLifetime;
            if (
                isHistorical
                || hasTerminalEvent
                || endedCallIdsRef.current.has(callId)
                || callStateRef.current.status !== CALL_STATES.IDLE
            ) {
                return;
            }

            setActiveCallChat(eventChat);
            openDedicatedCallWindow({
                silent: true,
                descriptor: {
                    type: "inbound",
                    chat: eventChat,
                    meetingUuid: eventChat.meetingUuid,
                    sender,
                    eventId,
                    callId,
                    hasNotes: sharedSummaryUpdate?.hasNotes
                }
            });
            updateCallState({
                ...INITIAL_CALL_STATE,
                status: CALL_STATES.INBOUND,
                callId,
                callIds: callId ? [callId] : [],
                direction: "inbound",
                sender,
                eventId,
                isGroupCall: Boolean(eventChat.isGroupChat),
                groupCallId: callId,
                matrixRoomId,
                chatUuid: eventChat.uuid,
                hasNotes: sharedSummaryUpdate?.hasNotes
            });
            updateActiveGroupCall({
                callId,
                groupCallId: callId,
                sender,
                eventId,
                callType: VIDEO_CALL_TYPE,
                chat: eventChat,
                isActive: true,
                isGroupCall: Boolean(eventChat.isGroupChat),
                matrixRoomId,
                chatUuid: eventChat.uuid,
                startedAt: event.getTs(),
                hasNotes: sharedSummaryUpdate?.hasNotes
            });
        };

        const handleDecryptedMatrixRtcNotification = (event, error) => {
            if (error) {
                return;
            }

            const room = matrixClient.getRoom(event.getRoomId?.());
            handleMatrixRtcNotification(event, room, false, false, { liveEvent: true });
        };

        matrixClient.on("Room.timeline", handleMatrixRtcNotification);
        matrixClient.on(MatrixEventEvent.Decrypted, handleDecryptedMatrixRtcNotification);
        return () => {
            matrixClient.off("Room.timeline", handleMatrixRtcNotification);
            matrixClient.off(MatrixEventEvent.Decrypted, handleDecryptedMatrixRtcNotification);
        };
    }, [
        currentUser,
        currentUserId,
        endCall,
        getChatByRoomId,
        matrixClient,
        openDedicatedCallWindow,
        setActiveCallChat,
        updateActiveGroupCall,
        updateCallState
    ]);

    useEffect(() => {
        const sessionManager = matrixClient?.matrixRTC;
        if (!sessionManager) {
            return undefined;
        }

        const publishActiveSession = (matrixRoomId, session) => {
            if (!session?.memberships?.length) {
                return;
            }
            const chat = getChatByRoomId(matrixRoomId);
            if (!chat) {
                return;
            }
            const oldestMembership = session.getOldestMembership?.() || session.memberships[0];
            updateActiveGroupCall({
                callId: session.callId || "",
                groupCallId: session.callId || "",
                sender: oldestMembership?.sender || "",
                eventId: oldestMembership?.eventId || "",
                callType: VIDEO_CALL_TYPE,
                chat,
                isActive: true,
                isGroupCall: Boolean(chat.isGroupChat),
                matrixRoomId,
                chatUuid: chat.uuid,
                startedAt: oldestMembership?.createdTs?.() || Date.now()
            });
        };
        const clearActiveSession = (matrixRoomId) => {
            if (activeGroupCallRef.current?.matrixRoomId === matrixRoomId) {
                updateActiveGroupCall(null);
            }
        };

        sessionManager.on("session_started", publishActiveSession);
        sessionManager.on("session_ended", clearActiveSession);
        (activeChatList || []).forEach(chat => {
            const room = matrixClient.getRoom(chat.matrixRoomId);
            const session = room ? sessionManager.getActiveRoomSession(room) : null;
            if (session) {
                publishActiveSession(chat.matrixRoomId, session);
            }
        });

        return () => {
            sessionManager.off("session_started", publishActiveSession);
            sessionManager.off("session_ended", clearActiveSession);
        };
    }, [activeChatList, getChatByRoomId, matrixClient, updateActiveGroupCall]);

    useEffect(() => {
        if (!matrixClient) {
            return undefined;
        }

        const handleCallNoteEvent = event => {
            if (!event?.getRoomId?.()) {
                return;
            }
            if ([
                EventStatus.CANCELLED,
                EventStatus.ENCRYPTING,
                EventStatus.NOT_SENT,
                EventStatus.QUEUED,
                EventStatus.SENDING
            ].includes(event.status)) {
                return;
            }

            const sender = event.getSender();
            const content = event.getContent() || {};
            const activeCallState = callStateRef.current || {};
            const activeCallId = getCurrentCallNotesKey(activeCallState);
            const activeChat = getActiveCallChat();
            const isMeetingRoomNote = Boolean(
                activeChat?.originatingMatrixRoomId
                && event.getRoomId?.() === activeChat.matrixRoomId
                && event.getType() === ROOM_MESSAGE
                && content.msgtype === "m.text"
                && typeof content.body === "string"
                && content.body.trim()
            );
            const isLegacyNote = [CALL_EVENT_TYPES.NOTE, CALL_EVENT_TYPES.LEGACY_NOTE]
                .includes(event.getType())
                && Boolean(content.note || content[CALL_NOTE_MESSAGE_FIELD]);
            if (!isMeetingRoomNote && !isLegacyNote) {
                return;
            }
            const participantName = matrixClient.getRoom(event.getRoomId())?.getMember(sender)?.name
                || matrixClient.getUser(sender)?.displayName
                || "";
            const note = isMeetingRoomNote
                ? {
                    id: event.getTxnId?.() || event.getId() || `${event.getRoomId()}-${event.getTs()}`,
                    callId: activeCallId,
                    body: content.body.trim(),
                    authorName: getCallParticipantLabel(sender, participantName),
                    authorUserId: sender || "",
                    createdAt: event.getTs(),
                    matrixRoomId: event.getRoomId(),
                    referenceEventId: ""
                }
                : getCallNoteFromContent({
                    ...content,
                    author_user_id: content.author_user_id || sender,
                    matrix_room_id: event.getRoomId?.() || ""
                });
            if (note) {
                updateCallState(previous => ({ ...previous, hasNotes: true }));
                updateActiveGroupCall(previous => previous ? { ...previous, hasNotes: true } : previous);
                publishHasNotesUpdate({
                    targetChat: activeChat,
                    callId: activeCallId || note.callId,
                    noteRoomId: event.getRoomId()
                });
                const storedNote = isMeetingRoomNote && activeCallId ? {
                    ...note,
                    callId: activeCallId,
                    matrixRoomId: activeChat.matrixRoomId,
                    referenceEventId: activeCallState.eventId || note.referenceEventId
                } : note;
                upsertCallNote(storedNote);
                if (
                    activeCallState.status === CALL_STATES.ON_CALL &&
                    activeCallState.matrixRoomId === event.getRoomId?.() &&
                    activeCallId &&
                    activeCallId !== storedNote.callId
                ) {
                    upsertCallNote({ ...storedNote, callId: activeCallId });
                }
            }
        };

        const handleClientEvent = event => {
            handleCallNoteEvent(event);
        };

        const handleDecryptedEvent = (event, error) => {
            if (error || ![CALL_EVENT_TYPES.NOTE, ROOM_MESSAGE].includes(event?.getType?.())) {
                return;
            }

            handleCallNoteEvent(event);
        };

        matrixClient.on(ClientEvent.Event, handleClientEvent);
        matrixClient.on(MatrixEventEvent.Decrypted, handleDecryptedEvent);
        return () => {
            matrixClient.off(ClientEvent.Event, handleClientEvent);
            matrixClient.off(MatrixEventEvent.Decrypted, handleDecryptedEvent);
        };
    }, [
        getActiveCallChat,
        getCurrentCallNotesKey,
        matrixClient,
        publishHasNotesUpdate,
        updateActiveGroupCall,
        updateCallState,
        upsertCallNote
    ]);

    useEffect(() => {
        if (callStateRef.current.status !== CALL_STATES.IDLE) {
            return;
        }

        stopMedia();
        updateActiveGroupCall(null);
        setActiveCallChat(null);
        updateCallState(previous => ({
            ...previous,
            status: CALL_STATES.IDLE,
            callId: "",
            callIds: [],
            groupCallId: "",
            matrixRoomId: "",
            chatUuid: "",
            startedAt: 0
        }));
        setCallLobby(previous => ({
            ...INITIAL_CALL_LOBBY_STATE,
            popupBlocked: previous.popupBlocked
        }));
    }, [openedMatrixChat?.uuid, setActiveCallChat, stopMedia, updateActiveGroupCall, updateCallState]);

    useEffect(() => {
        return () => {
            meetingPreparationRequestIdRef.current += 1;
            stopMedia();
        };
    }, [stopMedia]);

    const currentCallNotesKey = getCurrentCallNotesKey(callState);
    const callNotes = useMemo(() => {
        const callNoteRoomIds = new Set([
            callState.matrixRoomId,
            callChat?.originatingMatrixRoomId
        ].filter(Boolean));

        return Array.from(new Map(
            Object.values(callNotesByCallId)
                .flat()
                .filter(note =>
                    callNoteRoomIds.has(note.matrixRoomId) &&
                    (
                        note.callId === currentCallNotesKey ||
                        (callState.eventId && note.referenceEventId === callState.eventId)
                    )
                )
                .map(note => [note.id, note])
        ).values()).sort((firstNote, secondNote) => firstNote.createdAt - secondNote.createdAt);
    }, [
        callChat?.originatingMatrixRoomId,
        callNotesByCallId,
        callState.eventId,
        callState.matrixRoomId,
        currentCallNotesKey
    ]);

    const handleCallWindowStatus = useCallback((nextStatus) => {
        if (!nextStatus) {
            return;
        }
        if (Array.isArray(nextStatus.participantUserIds)) {
            callParticipantIdsRef.current = new Set(nextStatus.participantUserIds);
        }

        if (nextStatus.status === "ended") {
            updateCallState(previous => ({
                ...previous,
                ...nextStatus,
                status: nextStatus.startedAt ? CALL_STATES.ON_CALL : previous.status
            }));
            endCall(
                nextStatus.action || "hangup",
                "user_hangup",
                {
                    preserveActiveCall: nextStatus.callStillActive === true,
                    publishSummary: nextStatus.publishSummary !== false,
                    publishTerminalNotification: false
                }
            );
            return;
        }
        if (nextStatus.status === "failed") {
            Toast.error(nextStatus.error || "Unable to join the call.");
            endCall("failed");
            return;
        }

        const nextChat = getChatByRoomId(nextStatus.matrixRoomId);
        if (nextChat) {
            setActiveCallChat(nextChat);
        }
        updateCallState(previous => ({ ...previous, ...nextStatus }));
        updateActiveGroupCall({
            ...nextStatus,
            chat: nextChat || activeGroupCallRef.current?.chat,
            isActive: true
        });
    }, [
        endCall,
        getChatByRoomId,
        setActiveCallChat,
        updateActiveGroupCall,
        updateCallState
    ]);

    useEffect(() => {
        callWindowBridgeRef.current.update({
            matrixClient,
            headers,
            currentUser,
            callNotes,
            isSendingCallNote,
            addCallNote,
            reportStatus: handleCallWindowStatus
        });
    }, [
        addCallNote,
        callNotes,
        currentUser,
        handleCallWindowStatus,
        headers,
        isSendingCallNote,
        matrixClient
    ]);

    return {
        callState,
        callNotes,
        activeGroupCall,
        isSendingCallAction,
        isSendingCallNote,
        localStream,
        localScreenShareStream,
        remoteStream: remoteStreams[0]?.stream || null,
        remoteStreams,
        callChat,
        callLobby,
        callWindowRef,
        screenShareIsActive,
        rememberKnownChats,
        startCall,
        openCallLobby,
        closeCallLobby,
        focusCallWindow,
        startCallFromLobby,
        setLocalAudioEnabled,
        setLocalMicrophoneDevice,
        setLocalVideoEnabled,
        setLocalCameraDevice,
        setLocalSpeakerDevice,
        toggleScreenShare,
        setLocalBackgroundEffect,
        addCallNote,
        answerCall,
        answerCallFromLobby,
        joinGroupCall,
        hangupCall,
        rejectCall
    };
};
